import { useSelector } from "react-redux";
import MainContentLayout from "../../components/mainContentLayout";
import ItemCard from "../../components/itemCard";

import { FEATURES } from "../../config/features.config";
import { resolveFeatureAccess } from "../../utils/featureAccess";
import icons from "../../config/iconMap";

import {
  isAdminOnly,
  isCoachOnly,
  isPlayerOnly,
  isDualRole,
} from "../../utils/roleHelpers";

// -------------------------------
// Divider
// -------------------------------
const Divider = ({ label }) => (
  <div className="flex items-center my-6">
    <div className="flex-1 h-px bg-gray-300" />
    <span className="px-3 text-xs text-gray-500 uppercase tracking-wider">
      {label}
    </span>
    <div className="flex-1 h-px bg-gray-300" />
  </div>
);

export default function Dashboard() {
  const user = useSelector((state) => state.user.user);

  // normalize roles
  const roles = Array.isArray(user?.role)
    ? user.role
    : [user?.role].filter(Boolean);

  const hasCoach = !!user?.coachId;

  // ----------------------------------
  // Enrich features
  // ----------------------------------
  const items = FEATURES
    .map((feature) => {
      const access = resolveFeatureAccess(feature, user);
      if (!access.visible) return null;

      return {
        ...feature,
        ...access,
        icon: icons[feature.icon],
      };
    })
    .filter(Boolean);

  // ----------------------------------
  // PLAYER ONLY
  // ----------------------------------
  if (isPlayerOnly(roles)) {
    const mainItems = items.filter(
      (i) => !i.isCoachProvided
    );

    const coachCornerItems = items.filter(
      (i) => i.isCoachProvided
    );

    return (
      <MainContentLayout>
        <h1 className="text-3xl font-bold text-center my-6">Dashboard</h1>

        {/* Player main area */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
          {mainItems.map((item) => (
            <ItemCard key={item.id} item={item} user={user} />
          ))}
        </div>

        {/* Coach Related Corner */}
        {coachCornerItems.length > 0 && (
          <>
            <Divider label="Coach Related Corner" />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
              {coachCornerItems.map((item) => (
                <ItemCard key={item.id} item={item} user={user} />
              ))}
            </div>
          </>
        )}
      </MainContentLayout>
    );
  }

  // ----------------------------------
  // COACH ONLY
  // ----------------------------------
  if (isCoachOnly(roles)) {
    return (
      <MainContentLayout>
        <h1 className="text-3xl font-bold text-center my-6">Dashboard</h1>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
          {items
            .filter(
              (i) =>
                i.section === "coach" ||
                i.section === "general"
            )
            .map((item) => (
              <ItemCard key={item.id} item={item} user={user} />
            ))}
        </div>
      </MainContentLayout>
    );
  }

  // ----------------------------------
  // ADMIN ONLY
  // ----------------------------------
  if (isAdminOnly(roles)) {
    return (
      <MainContentLayout>
        <h1 className="text-3xl font-bold text-center my-6">Dashboard</h1>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
          {items
            .filter(
              (i) =>
                i.section === "admin" ||
                i.section === "general"
            )
            .map((item) => (
              <ItemCard key={item.id} item={item} user={user} />
            ))}
        </div>
      </MainContentLayout>
    );
  }

  // ----------------------------------
  // DUAL ROLE (ADMIN + COACH)
  // ----------------------------------
  if (isDualRole(roles)) {
    return (
      <MainContentLayout>
        <h1 className="text-3xl font-bold text-center my-6">Dashboard</h1>

        {["general", "coach", "admin"].map((section) => {
          const sectionItems = items.filter((item) => {
          // coach-provided goes to COACH in dual-role
            const effectiveSection = item.isCoachProvided ? "coach" : item.section;

            return effectiveSection === section;
          });
          if (!sectionItems.length) return null;

          return (
            <div key={section}>
              <Divider label={section.toUpperCase()} />
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
                {sectionItems.map((item) => (
                  <ItemCard key={item.id} item={item} user={user} />
                ))}
              </div>
            </div>
          );
        })}
      </MainContentLayout>
    );
  }

  return null;
}
